import React from 'react';
import { Shield, Eye, Code, Lock, Github, Twitter, MessageCircle } from 'lucide-react';

export const PrivacyView = () => {
  return (
    <div className="space-y-12">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-block bg-green-400 text-black px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000] mb-6">
          <h1 className="text-4xl md:text-6xl font-black uppercase font-mono">
            PRIVACY POLICY
          </h1>
        </div>
        <div className="bg-white text-black p-8 border-4 border-black shadow-[8px_8px_0_0_#000] max-w-2xl mx-auto">
          <p className="text-3xl font-black uppercase mb-2 font-mono">
            WE COLLECT NOTHING
          </p>
          <p className="text-xl font-mono">
            This is the entire privacy policy.
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Why Section */}
        <div className="bg-purple-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
          <div className="flex items-center gap-3 mb-6">
            <Shield size={32} />
            <h2 className="text-2xl font-black uppercase font-mono">WHY?</h2>
          </div>
          <div className="space-y-4 text-lg font-mono">
            <p>
              The entire application is a <strong>static frontend</strong> that talks directly to Ethereum smart contracts.
            </p>
            <p>
              There is literally <strong>no backend</strong> to send data to.
            </p>
            <p className="text-yellow-300 font-black pt-4 border-t-2 border-white/30">
              We can't collect data. The architecture prevents it.
            </p>
          </div>
        </div>

        {/* What We Don't Know */}
        <div className="bg-red-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
          <div className="flex items-center gap-3 mb-6">
            <Eye size={32} className="opacity-50" />
            <h2 className="text-2xl font-black uppercase font-mono">WE DON'T KNOW</h2>
          </div>
          <ul className="space-y-2 text-lg font-mono">
            <li>❌ Who you are</li>
            <li>❌ Where you're located</li>
            <li>❌ What browser you use</li>
            <li>❌ How you found this site</li>
            <li>❌ What you do here</li>
            <li>❌ How many times you've visited</li>
            <li>❌ Whether you minted/staked</li>
            <li>❌ Literally anything about you</li>
          </ul>
        </div>
      </div>

      {/* What IS Public */}
      <div className="bg-cyan-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <h2 className="text-2xl font-black uppercase mb-6 font-mono">
          ⚠️ WHAT IS PUBLIC (BLOCKCHAIN)
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-lg font-mono">
            <p>When you interact with Ethereum, certain info is <strong>publicly visible on the blockchain</strong>:</p>
            <ul className="space-y-2 pl-4">
              <li>✓ Your wallet address</li>
              <li>✓ Transaction history</li>
              <li>✓ NFT ownership</li>
              <li>✓ POGE balance</li>
            </ul>
          </div>
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <p className="font-black mb-3 text-yellow-300">BUT THIS DATA IS:</p>
            <ul className="space-y-2 text-sm">
              <li>→ Not collected by us</li>
              <li>→ Not on our servers (we have none)</li>
              <li>→ Pseudonymous</li>
              <li>→ Your choice (you transacted)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Verification Grid */}
      <div>
        <h2 className="text-3xl font-black mb-6 uppercase text-white font-mono text-center border-b-4 border-white pb-4">
          HOW TO VERIFY
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Network Tab */}
          <div className="bg-white text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
            <div className="flex justify-center mb-4">
              <Eye size={40} className="text-blue-600" />
            </div>
            <h3 className="font-black text-xl mb-3 uppercase text-center font-mono">
              NETWORK TAB
            </h3>
            <p className="text-sm font-mono mb-3">
              Open browser DevTools (F12) → Network
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-green-600 font-black">✓</span>
                <span>RPC calls to Ethereum</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-600 font-black">✗</span>
                <span>No analytics calls</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-red-600 font-black">✗</span>
                <span>No tracking pixels</span>
              </div>
            </div>
          </div>

          {/* Source Code */}
          <div className="bg-white text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
            <div className="flex justify-center mb-4">
              <Code size={40} className="text-purple-600" />
            </div>
            <h3 className="font-black text-xl mb-3 uppercase text-center font-mono">
              SOURCE CODE
            </h3>
            <p className="text-sm font-mono mb-3">
              All code is open source on GitHub
            </p>
            <a 
              href="https://github.com/dunkpoge"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-black text-white font-black py-3 px-4 text-center border-2 border-black hover:bg-gray-900 transition-all text-sm uppercase"
            >
              VIEW ON GITHUB →
            </a>
          </div>

          {/* Run Locally */}
          <div className="bg-white text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
            <div className="flex justify-center mb-4">
              <Lock size={40} className="text-green-600" />
            </div>
            <h3 className="font-black text-xl mb-3 uppercase text-center font-mono">
              RUN LOCALLY
            </h3>
            <p className="text-sm font-mono mb-3">
              Clone the repo, run on your machine
            </p>
            <div className="bg-gray-100 p-3 border-2 border-gray-400 text-xs font-mono">
              <code>
                git clone https://github.com/dunkpoge<br/>
                npm install<br/>
                npm start
              </code>
            </div>
          </div>
        </div>
      </div>

      {/* Cookies & Storage */}
      <div className="bg-yellow-400 text-black p-8 border-4 border-black shadow-[8px_8px_0_0_#000]">
        <h2 className="text-2xl font-black uppercase mb-4 font-mono">
          COOKIES & LOCAL STORAGE
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="font-mono">
            <p className="font-black mb-2">WE USE localStorage ONLY FOR:</p>
            <ul className="space-y-2 text-sm">
              <li>→ Wallet connection preference (optional)</li>
              <li>→ Caching RPC responses (performance)</li>
            </ul>
            <p className="mt-4 text-sm">
              This data <strong>never leaves your browser</strong>. Clear it anytime in browser settings.
            </p>
          </div>
          <div className="bg-black text-white p-6 border-2 border-black">
            <p className="font-black text-red-400 mb-3 uppercase">NO:</p>
            <ul className="space-y-1 text-sm font-mono">
              <li>✗ Tracking cookies</li>
              <li>✗ Session cookies</li>
              <li>✗ Third-party cookies</li>
              <li>✗ Analytics cookies</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Legal Compliance */}
      <div className="bg-gradient-to-br from-blue-600 to-purple-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <h2 className="text-2xl font-black uppercase mb-6 font-mono text-center">
          LEGAL COMPLIANCE
        </h2>
        <div className="max-w-3xl mx-auto space-y-4 font-mono">
          <div className="bg-black/30 p-4 border-2 border-white/50">
            <p className="font-black text-lg mb-2">GDPR (Europe):</p>
            <p className="text-sm">No data = no compliance burden. Nothing to request, export, or delete.</p>
          </div>
          <div className="bg-black/30 p-4 border-2 border-white/50">
            <p className="font-black text-lg mb-2">CCPA (California):</p>
            <p className="text-sm">We have no data to sell, share, or delete.</p>
          </div>
          <div className="bg-black/30 p-4 border-2 border-white/50">
            <p className="font-black text-lg mb-2">Other Privacy Laws:</p>
            <p className="text-sm">Laws regulate data collection. We collect nothing.</p>
          </div>
          <p className="text-center text-xl font-black text-yellow-300 pt-4">
            PRIVACY BY DESIGN, NOT PRIVACY BY PROMISE
          </p>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-black text-white p-8 border-4 border-green-400 shadow-[8px_8px_0_0_#00ff00]">
        <h2 className="text-2xl font-black uppercase mb-6 font-mono text-center">
          CONTACT
        </h2>
        <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
          <a 
            href="https://github.com/dunkpoge"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-3 px-6 border-4 border-white hover:bg-gray-200 transition-all flex items-center gap-2 no-underline"
          >
            <Github size={20} />
            GITHUB
          </a>
          <a 
            href="https://twitter.com/dunkpoge"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-3 px-6 border-4 border-white hover:bg-gray-200 transition-all flex items-center gap-2 no-underline"
          >
            <Twitter size={20} />
            TWITTER
          </a>
          <a 
            href="https://discord.gg/7PsZwC3TZX"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-black font-black py-3 px-6 border-4 border-white hover:bg-gray-200 transition-all flex items-center gap-2 no-underline"
          >
            <MessageCircle size={20} />
            DISCORD
          </a>
        </div>
        <p className="text-center mt-6 text-sm font-mono opacity-75">
          But honestly, there's not much to discuss. We collect nothing.
        </p>
      </div>

      {/* Philosophical Note */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white p-8 border-4 border-white shadow-[12px_12px_0_0_#fff]">
        <h2 className="text-3xl font-black uppercase mb-6 font-mono text-center">
          PHILOSOPHICAL NOTE
        </h2>
        <div className="max-w-3xl mx-auto space-y-4 text-lg font-mono text-center">
          <p className="text-2xl font-black text-yellow-300">
            PRIVACY IS SOVEREIGNTY
          </p>
          <p>
            If you shouldn't trust us with your assets (hence trustless smart contracts), 
            you shouldn't have to trust us with your data either.
          </p>
          <p className="text-xl font-black pt-4 border-t-4 border-white/50">
            This is what true decentralization looks like: we don't even know you exist unless you transact on-chain.
          </p>
          <p className="text-sm opacity-90 italic">
            And even then, we only know a wallet address interacted with a smart contract. We don't know it's <em>you</em>.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white text-black p-8 border-4 border-black shadow-[12px_12px_0_0_#000]">
        <h2 className="text-3xl font-black uppercase mb-6 font-mono text-center">
          SUMMARY
        </h2>
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {[
            'Zero data collection',
            'Static frontend (no backend)',
            'Open source (verify yourself)',
            'No analytics, cookies, or tracking',
            'Blockchain data is public (inherent to Ethereum)',
            'You can verify all of this'
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-green-100 border-2 border-green-600">
              <span className="text-2xl">✅</span>
              <span className="font-bold font-mono">{item}</span>
            </div>
          ))}
        </div>
        <p className="text-center mt-8 text-2xl font-black font-mono uppercase">
          That's it. That's the privacy policy.
        </p>
      </div>

      {/* Bottom Badge */}
      <div className="text-center">
        <div className="inline-block bg-green-400 text-black px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000]">
          <p className="text-xl font-black font-mono uppercase">
            Much Privacy • Very Sovereign • Wow
          </p>
        </div>
      </div>
    </div>
  );
};